<template>
	<div class="tq-form-content">
		<el-form ref="tqFormRef" :rules="rules" :label-position="labelPosition" :model="ruleFormObj"
			:labelSuffix="labelSuffix" :labelWidth="labelWidth">
			<template v-for="(list, index) in formItems" :key="index">
				<div class="formcontrol_form-title-border" v-if="!onlyForm">
					<span class="formcontrol_form-title-image"></span>
					<span class="formcontrol_form-title text-medium-primary">{{ list.formTitle }}</span>
				</div>
				<div class="formcontrol_form-item_border" :class="{ 'only-form': onlyForm }">
					<el-row :gutter="50">
						<template v-for="item in list.formContentList" :key="item.compKey">
							<el-col v-if="item.fromtype === 'input'" :span="item.span" :name="item.name">
								<el-form-item :label="item.label" :required="item.required" :prop="item.compKey">
									<el-input v-bind="item" :model-value="item.value" @input="onItemChange($event, item)" />
								</el-form-item>
							</el-col>
							<el-col v-if="item.fromtype === 'datePicker'" :span="item.span">
								<el-form-item  :label="item.label" :required="item.required" :prop="item.compKey">
									<el-date-picker v-bind="item" v-model="item.value" @change="onItemChange($event, item)" />
								</el-form-item>
							</el-col>
							<el-col v-if="item.fromtype === 'select'" :span="item.span">
								<el-form-item :label="item.label" :required="item.required" :prop="item.compKey">
									<el-select v-bind="item" v-model="item.value" @change="onItemChange($event, item)">
										<el-option v-for="innerItem in item.options" :label="innerItem.label"
											:value="innerItem.value" :key="innerItem.value" />
									</el-select>
								</el-form-item>
							</el-col>
							<el-col v-if="item.fromtype === 'radioGroup'" :span="item.span">
								<el-form-item :label="item.label" :required="item.required" :prop="item.compKey"
									class="tq-form-radio-group">
									<el-radio-group v-bind="item" v-model="item.value" @input="onItemChange($event, item)">
										<el-radio v-for="radioItem in item.options" :value="radioItem.value"
											:key="radioItem.value">
											{{ radioItem.label }}
										</el-radio>
									</el-radio-group>
								</el-form-item>
							</el-col>
							<el-col v-if="item.fromtype === 'custom'" :span="item.span">
								<el-form-item class="formcontrol_form-item_slot_title" :prop="item.compKey"
									:label="item.title" :required="item.required">
									<div class="formcontrol_form-item_slot">
										<slot :name=item.name v-bind:item="item" />
									</div>
								</el-form-item>
							</el-col>
						</template>
					</el-row>
				</div>
			</template>
		</el-form>
		<div class="formcontrol_form-item_button" v-if="buttonConfig.leftButtonShow || buttonConfig.rightButtonShow">
			<el-button v-if="buttonConfig.leftButtonShow" class="formcontrol_form-item_button_left"
				@click="formClose">{{
			buttonConfig.leftButtonText }}</el-button>
			<el-button v-if="buttonConfig.rightButtonShow" class="formcontrol_form-item_button_right" type="primary"
				@click="formSubmit">
				{{ buttonConfig.rightButtonText }}
			</el-button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, defineProps, computed, watch } from 'vue'
import type { FormItem, ButtonConfig, rule, FormListItem, formKeyWithValues } from './interface'
import type { FormInstance, FormRules } from 'element-plus'

export interface Props {
	labelSuffix?: string
	onlyForm?: boolean
	labelPosition?: string
	labelWidth?: string
	formItems: FormItem[]
	formKeyWithValues?: formKeyWithValues
	buttonConfig?: ButtonConfig
}

const props = withDefaults(defineProps<Props>(), {
  labelSuffix: ':',
  onlyForm: false,
  labelPosition: 'left',
  labelWidth: '70px',
  formItems: () => [],
  formKeyWithValues: () => ({}),
  buttonConfig: () => ({ leftButtonShow: true, leftButtonText: '取消', rightButtonShow: true, rightButtonText: '确认' })
})

const emit = defineEmits(['Itemchanged', 'formSubmit', 'formClose'])

const rules = computed((): FormRules => {
  const tmpRules: { [key: string]: rule[] } = {}
  props.formItems.forEach((el: FormItem) => {
    el.formContentList.forEach((en: FormListItem) => {
      if (en && en.rules) {
        tmpRules[en.compKey] = en.rules
      }
    })
  })
  return tmpRules
})

const ruleFormObj = computed(() => {
  const tmpObj: { [key: string]: unknown } = {}
  props.formItems.forEach((item: FormItem) => {
    const element = item
    element.formContentList.forEach((el: FormListItem) => {
      tmpObj[el.compKey] = el.value
    })
  })
  return tmpObj
})

const onItemChange = (event: string | string[], item: FormListItem) => {
  console.log('event', event)

  item.value = event
  emit('Itemchanged', ruleFormObj.value)
  item.onChanged && item.onChanged(event)
}

const tqFormRef = ref<FormInstance>()
const formSubmit = () => {
  tqFormRef.value && tqFormRef.value.validate((valid: boolean) => {
    emit('formSubmit', valid, ruleFormObj.value)
  })
}

type formDataType = {
	valid: boolean
	data: unknown
}
type Fun = (formData: formDataType) => void

const formQueryAll = (validFuc: Fun) => {
  if (!validFuc) return
  tqFormRef.value && tqFormRef.value.validate((valid: boolean) => {
    const formData: formDataType = { valid, data: ruleFormObj.value }
    validFuc(formData)
  })
}

const formClose = () => {
  emit('formClose', ruleFormObj.value)
}

watch(() => props.formKeyWithValues, (newVal: formKeyWithValues) => {
  props.formItems.forEach((item: FormItem) => {
    item.formContentList.forEach((el: FormListItem) => {
      if (newVal[el.compKey] !== undefined && newVal[el.compKey] !== null) {
        el.value = newVal[el.compKey]
      }
    })
  })
}, { immediate: true, deep: true })

defineExpose({
  formQueryAll
})
defineOptions({
  name: 'TqForm'
})
</script>

<style lang="stylus">
.tq-form-content {
    width: 100%;
    margin: auto;
    background: #FFFFFF;
    .formcontrol_form-title-border{
        padding:13px 0px;
        display: flex;
        align-items: center;
        .formcontrol_form-title-image{
            width: 6px;height: 14px;
            margin-right: 8px;
            margin-left: 10px;
            display: inline-block;
            background-image: linear-gradient(180deg, #FFDD65 0%, #F9CB68 100%);
        }

    }
    .formcontrol_form-item_border{
        border: 1px solid rgba(230,230,230,1);
        border-radius: 10px;
        &.only-form {
            padding: 0;
            border: none;
        }
        padding:30px 20px;
        padding-bottom: 12px;
        margin: 0 auto;
        margin-bottom: 20px;
        .el-form-item__label{
            font-size: var(--text-size-medium);
            color: var(--grey-degree-8);
            padding: 0;
            margin-right:var(--space-component);
        }
        .el-form-item .is-required .el-form-item--small{
            vertical-align: middle;
        }
        .el-form-item__content {
            display: flex;
            .el-select, .el-date-editor.el-input {
                width: 100%;
            }
        }
        .formcontrol_form-item_slot{
            width: 100%;
            overflow: auto;
        }
    }
    .formcontrol_form-item_button {
        text-align: center;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .tq-form-radio-group  {
        .el-form-item__content {
            padding-top: 8px;
        }
    }
}
</style>
